import { baseUrlImages } from '@src/config/urls'
import { MerchantType } from '@src/graphql-types'
import { OutletFulfilmentContextData } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/context/types'
import { MarketplaceContextData } from '@src/hooks/useMarketplace'

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }

export type StructuredData = { [key: string]: JsonValue }

const SCHEMA_CONTEXT = 'https://schema.org'

const RATING_SCALE_MIN = 1
const RATING_SCALE_MAX = 5

// Keyed by ISO-8601 weekday, matching the `day` field on OpeningTime.
const SCHEMA_DAY_BY_ISO_DAY: Record<number, string | undefined> = {
  1: 'https://schema.org/Monday',
  2: 'https://schema.org/Tuesday',
  3: 'https://schema.org/Wednesday',
  4: 'https://schema.org/Thursday',
  5: 'https://schema.org/Friday',
  6: 'https://schema.org/Saturday',
  7: 'https://schema.org/Sunday',
}

// A retail outlet is not a FoodEstablishment, so servesCuisine is not a valid
// property for it and the validator rejects it.
const OUTLET_SCHEMA_BY_MERCHANT_TYPE: Record<
  MerchantType,
  { type: string; idFragment: string; categoryProperty: string }
> = {
  [MerchantType.RESTAURANT]: {
    type: 'Restaurant',
    idFragment: 'restaurant',
    categoryProperty: 'servesCuisine',
  },
  [MerchantType.RETAIL]: {
    type: 'Store',
    idFragment: 'store',
    categoryProperty: 'knowsAbout',
  },
}

// react-helmet writes JSON-LD via innerHTML, so an unescaped `<` would let tenant text close the tag.
export const serialiseStructuredData = (data: StructuredData): string =>
  JSON.stringify(data).replace(/</g, '\\u003c')

const isPresent = <T>(value: T): value is NonNullable<T> => {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  return true
}

const withoutEmptyValues = (data: StructuredData): StructuredData =>
  Object.fromEntries(
    Object.entries(data).filter(([, value]) => isPresent(value))
  )

const joinAddressLines = (lines: (string | null)[]): string =>
  lines.filter(isPresent).join(', ')

const toImageUrl = (image: string | null): string | null =>
  image ? `${baseUrlImages}/${image}` : null

type MarketplaceStructuredDataInput = Pick<
  MarketplaceContextData,
  | 'name'
  | 'title'
  | 'metaDescription'
  | 'ietf'
  | 'logoImage'
  | 'companyLegalName'
  | 'companyNumber'
  | 'vatNumber'
  | 'supportEmail'
  | 'supportPhone'
  | 'socialURLFacebook'
  | 'socialURLInstagram'
  | 'socialURLTikTok'
  | 'socialURLTwitter'
  | 'contactAddress'
>

export const buildMarketplaceStructuredData = (
  marketplace: MarketplaceStructuredDataInput,
  siteOrigin: string
): StructuredData => {
  const organisationId = `${siteOrigin}/#organization`
  const websiteId = `${siteOrigin}/#website`
  const { contactAddress } = marketplace

  return {
    '@context': SCHEMA_CONTEXT,
    '@graph': [
      withoutEmptyValues({
        '@type': 'WebSite',
        '@id': websiteId,
        url: siteOrigin,
        name: marketplace.title || marketplace.name,
        description: marketplace.metaDescription,
        inLanguage: marketplace.ietf,
        publisher: { '@id': organisationId },
      }),
      withoutEmptyValues({
        '@type': 'Organization',
        '@id': organisationId,
        name: marketplace.companyLegalName || marketplace.name,
        url: siteOrigin,
        logo: toImageUrl(marketplace.logoImage),
        email: marketplace.supportEmail,
        telephone: marketplace.supportPhone,
        vatID: marketplace.vatNumber,
        identifier: marketplace.companyNumber,
        address: withoutEmptyValues({
          '@type': 'PostalAddress',
          streetAddress: joinAddressLines([
            contactAddress.firstLine,
            contactAddress.secondLine,
            contactAddress.thirdLine,
          ]),
          addressLocality: contactAddress.city,
          postalCode: contactAddress.postcode,
          addressCountry: contactAddress.country?.name ?? null,
        }),
        sameAs: [
          marketplace.socialURLFacebook,
          marketplace.socialURLInstagram,
          marketplace.socialURLTikTok,
          marketplace.socialURLTwitter,
        ].filter(isPresent),
      }),
    ],
  }
}

type OutletStructuredDataInput = Pick<
  OutletFulfilmentContextData,
  | 'displayName'
  | 'description'
  | 'coverImage'
  | 'outletPhone'
  | 'outletCuisines'
  | 'outletAddress'
  | 'openingTimesArray'
  | 'reviewSummary'
  | 'restaurant'
>

const buildOpeningHoursSpecification = (
  openingTimes: OutletStructuredDataInput['openingTimesArray']
): StructuredData[] =>
  openingTimes.flatMap(({ start, end }) => {
    const dayOfWeek = SCHEMA_DAY_BY_ISO_DAY[start.day]
    if (!dayOfWeek) return []
    return [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek,
        opens: start.time,
        closes: end.time,
      },
    ]
  })

export const buildOutletStructuredData = (
  outlet: OutletStructuredDataInput,
  pageUrl: string,
  allowReviews: boolean,
  merchantType: MerchantType
): StructuredData => {
  const { outletAddress, reviewSummary } = outlet
  const showRating = Boolean(
    allowReviews && reviewSummary?.averageRating && reviewSummary.reviewCount
  )
  const { type, idFragment, categoryProperty } =
    OUTLET_SCHEMA_BY_MERCHANT_TYPE[merchantType]

  return withoutEmptyValues({
    '@context': SCHEMA_CONTEXT,
    '@type': type,
    '@id': `${pageUrl}#${idFragment}`,
    name: outlet.displayName,
    description: outlet.description || outlet.restaurant.metaDescription,
    image: toImageUrl(outlet.coverImage),
    url: pageUrl,
    telephone: outlet.outletPhone,
    [categoryProperty]: outlet.outletCuisines.map(({ name }) => name),
    address: withoutEmptyValues({
      '@type': 'PostalAddress',
      streetAddress: joinAddressLines([
        outletAddress.firstLine,
        outletAddress.secondLine,
        outletAddress.thirdLine,
      ]),
      addressLocality: outletAddress.city,
      postalCode: outletAddress.postcode,
      addressCountry: outletAddress.countryCode,
    }),
    openingHoursSpecification: buildOpeningHoursSpecification(
      outlet.openingTimesArray
    ),
    aggregateRating:
      showRating && reviewSummary
        ? {
            '@type': 'AggregateRating',
            ratingValue: reviewSummary.averageRating,
            reviewCount: reviewSummary.reviewCount,
            bestRating: RATING_SCALE_MAX,
            worstRating: RATING_SCALE_MIN,
          }
        : null,
  })
}
